import { useMemo } from 'react';

export default function MeetingStatsOverview({ history }) {
  const stats = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - 7);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const calculateStats = (startDate) => {
      const filtered = history.filter(item => {
        const itemDate = item.createdAt?.toDate?.() || new Date(item.createdAt);
        return itemDate >= startDate;
      });

      const totalDuration = filtered.reduce((sum, item) => {
        return sum + (item.duration || 0);
      }, 0);

      const totalParticipants = filtered.reduce((sum, item) => {
        return sum + (item.participantCount || 0);
      }, 0);

      const avgDuration = filtered.length > 0 ? Math.floor(totalDuration / filtered.length) : 0;

      return {
        count: filtered.length,
        totalDuration,
        avgDuration,
        totalParticipants,
        avgParticipants: filtered.length > 0 ? Math.floor(totalParticipants / filtered.length) : 0
      };
    };

    return {
      today: calculateStats(todayStart),
      week: calculateStats(weekStart),
      month: calculateStats(monthStart),
      total: {
        count: history.length,
        totalDuration: history.reduce((sum, item) => sum + (item.duration || 0), 0)
      }
    };
  }, [history]);

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const StatCard = ({ title, period, data, icon, gradient }) => (
    <div className={`bg-gradient-to-br ${gradient} rounded-3xl p-6 border border-white/10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-white/70 text-sm font-medium mb-1">{period}</p>
          <h3 className="text-3xl font-bold text-white">{data.count}</h3>
          <p className="text-white/60 text-xs mt-1">{data.count === 1 ? 'meeting' : 'meetings'}</p>
        </div>
        <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
          {icon}
        </div>
      </div>

      <div className="space-y-2 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-xs">Total time</span>
          <span className="text-white text-sm font-semibold">{formatDuration(data.totalDuration)}</span>
        </div>
        {data.avgDuration > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs">Avg duration</span>
            <span className="text-white text-sm font-semibold">{formatDuration(data.avgDuration)}</span>
          </div>
        )}
        {data.totalParticipants > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs">Total participants</span>
            <span className="text-white text-sm font-semibold">{data.totalParticipants}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 mb-8">
      {/* Overview Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white transition-colors">
            Meeting Overview
          </h3>
          <p className="text-sm text-[#86868b] dark:text-[#8e8e93] mt-1 transition-colors">
            Your activity summary across different time periods
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Today */}
        <StatCard
          title="Today"
          period="Today"
          data={stats.today}
          gradient="from-blue-500 to-blue-600"
          icon={
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />

        {/* This Week */}
        <StatCard
          title="Week"
          period="This Week"
          data={stats.week}
          gradient="from-purple-500 to-purple-600"
          icon={
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        />

        {/* This Month */}
        <StatCard
          title="Month"
          period="This Month"
          data={stats.month}
          gradient="from-green-500 to-green-600"
          icon={
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      </div>

      {/* Quick Stats */}
      <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#2c2c2e] transition-colors">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors">
              <svg className="w-6 h-6 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-[#1d1d1f] dark:text-white transition-colors">{stats.total.count}</p>
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1 transition-colors">Total Meetings</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-[#1d1d1f] dark:text-white transition-colors">
              {formatDuration(stats.total.totalDuration)}
            </p>
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1 transition-colors">Total Time</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-[#1d1d1f] dark:text-white transition-colors">{stats.week.count}</p>
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1 transition-colors">This Week</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-[#1d1d1f] dark:text-white transition-colors">
              {stats.month.avgParticipants || 0}
            </p>
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1 transition-colors">Avg Participants</p>
          </div>
        </div>
      </div>
    </div>
  );
}
